import { apiClient } from './api-client';
import { getAccessToken, readPersistedAuth } from '@/lib/tokenStorage';

// Helper to get tenant ID from auth storage (same logic as api-client)
const getTenantId = (): string => {
  try {
    const authStorage = readPersistedAuth();
    if (authStorage) {
      const parsed = JSON.parse(authStorage);
      if (parsed?.state?.user?.tenantId) {
        return parsed.state.user.tenantId;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return import.meta.env.VITE_DEFAULT_TENANT_ID || 'default';
};

// RAG Query types
export interface RagQueryDto {
  query: string;
  conversationId?: string;
  maxResults?: number;
  includeContext?: boolean;
  filters?: {
    categoryIds?: string[];
    articleIds?: string[];
    tags?: string[];
  };
}

export interface RagSource {
  id: string;
  type: 'article' | 'document';
  title: string;
  excerpt: string;
  relevanceScore: number;
  url?: string;
}

export interface RagResponse {
  answer: string;
  sources: RagSource[];
  confidence: number;
  conversationId?: string;
  followUpQuestions?: string[];
}

export interface RagSearchDto {
  query: string;
  limit?: number;
  filters?: {
    categoryIds?: string[];
    tags?: string[];
    status?: 'published';
  };
}

export interface RagSearchResult {
  id: string;
  type: 'article' | 'document';
  title: string;
  excerpt: string;
  score: number;
  category?: string;
  tags?: string[];
  url?: string;
}

export interface RagFeedbackDto {
  queryId: string;
  // Backend contract (rag.dto.ts SubmitFeedbackDto): `helpful` is the
  // required boolean field for a thumbs up/down; `rating` is a separate,
  // optional 1-5 scale this simple thumbs UI doesn't use. Sending the string
  // 'helpful'/'not_helpful' as `rating` (Bug #9621) failed validation with a
  // 400 on every submission, so every click silently lost the feedback.
  helpful: boolean;
  rating?: number;
  comment?: string;
}

export interface CanAnswerResponse {
  canAnswer: boolean;
  confidence: number;
  suggestedQuery?: string;
}

export const ragService = {
  /**
   * Query the RAG system with AI-powered response
   */
  async query(dto: RagQueryDto): Promise<RagResponse> {
    const response = await apiClient.post('/rag/query', dto);
    return response.data;
  },

  /**
   * Query with streaming response (SSE)
   */
  queryStream(dto: RagQueryDto, onChunk: (chunk: string) => void, onComplete: (response: RagResponse) => void): () => void {
    const controller = new AbortController();

    const fetchStream = async () => {
      try {
        const response = await fetch('/api/rag/query/stream', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAccessToken() ?? ''}`,
            'X-Tenant-Id': getTenantId(),
          },
          body: JSON.stringify(dto),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let fullResponse = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                continue;
              }
              try {
                const parsed = JSON.parse(data);

                // Handle content chunks
                if (parsed.type === 'content' && parsed.content) {
                  fullResponse += parsed.content;
                  onChunk(parsed.content);
                }

                // Handle completion (both formats: type='done' or complete=true)
                if (parsed.type === 'done' || parsed.complete) {
                  const responseData = parsed.response || parsed;
                  onComplete({
                    answer: fullResponse || responseData.answer || '',
                    sources: responseData.sources?.map((s: any) => ({
                      id: s.id,
                      type: s.type,
                      title: s.title,
                      excerpt: s.preview || s.excerpt || '',
                      relevanceScore: s.relevance || s.relevanceScore || 0,
                      url: s.slug ? `/knowledge/articles/${s.slug}` : undefined,
                    })) || [],
                    confidence: responseData.confidence || 0,
                    conversationId: responseData.queryId || parsed.conversationId,
                    followUpQuestions: responseData.followUpQuestions,
                  });
                }

                // Handle errors
                if (parsed.type === 'error') {
                  console.error('Stream error from server:', parsed.error);
                }
              } catch {
                // Ignore parse errors for incomplete chunks
              }
            }
          }
        }
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Stream error:', error);
        }
      }
    };

    fetchStream();

    return () => controller.abort();
  },

  /**
   * Search knowledge base without AI response
   */
  async search(dto: RagSearchDto): Promise<RagSearchResult[]> {
    const response = await apiClient.post('/rag/search', dto);
    return response.data;
  },

  /**
   * Check if a query can be answered
   */
  async canAnswer(query: string): Promise<CanAnswerResponse> {
    const response = await apiClient.get(`/rag/can-answer?query=${encodeURIComponent(query)}`);
    return response.data;
  },

  /**
   * Get query suggestions
   */
  async getSuggestions(query: string, limit = 5): Promise<string[]> {
    const response = await apiClient.get(`/rag/suggestions?query=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data;
  },

  /**
   * Submit feedback on a RAG response
   */
  async submitFeedback(dto: RagFeedbackDto): Promise<void> {
    await apiClient.post('/rag/feedback', dto);
  },
};

export default ragService;
